(function attachJudesNativeStartPrice(root, factory) {
  const pricing = root.JudesProbierPricing || (typeof module !== 'undefined' && module.exports
    ? require('./judes-probier-pricing') : null);
  const api = factory(root, pricing);
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  root.JudesNativeStartPrice = api;
}(typeof globalThis !== 'undefined' ? globalThis : this, function createJudesNativeStartPrice(root, pricing) {
  'use strict';

  const MAX_MANIFEST_BYTES = 120 * 1024;
  // The updater runs once per local calendar day. Six hours of bounded grace
  // prevents normal timer delay or a daylight-saving transition from creating
  // a daily seed gap while the exact per-load quote remains authoritative.
  const MAX_AGE_MS = 30 * 60 * 60 * 1000;
  const PRODUCT_ID = '6924162367677';
  const EXPECTED_COUNTRY_PRICE_GROUPS = Object.freeze({
    'DE|EUR': 'eur', 'AT|EUR': 'eur', 'ES|EUR': 'eur', 'IT|EUR': 'eur', 'FR|EUR': 'eur', 'NL|EUR': 'eur',
    'US|USD': 'usd', 'GB|GBP': 'gbp', 'CH|CHF': 'chf', 'CA|CAD': 'cad', 'DK|DKK': 'dkk'
  });
  const EXPECTED_RECIPE_IDS = Object.freeze(['trial-day-s1', 'trial-day-s2', 'trial-once-s1', 'trial-once-s2']);
  const identityFields = [
    'country', 'currency', 'priceGroupId', 'productId', 'recipeId', 'recipeVersion',
    'catalogVersion', 'pricingIdentityVersion', 'basketFingerprint', 'sourceDigest', 'sourceVersion'
  ];

  function byteLength(value) {
    if (typeof Buffer !== 'undefined') return Buffer.byteLength(value, 'utf8');
    return new TextEncoder().encode(value).byteLength;
  }

  function stable(value) {
    if (Array.isArray(value)) return value.map(stable);
    if (!value || typeof value !== 'object') return value;
    return Object.fromEntries(Object.keys(value).sort().map((key) => [key, stable(value[key])]));
  }

  function stableStringify(value) {
    return JSON.stringify(stable(value));
  }

  function fnv1a(value) {
    let hash = 0x811c9dc5;
    const bytes = typeof TextEncoder !== 'undefined'
      ? new TextEncoder().encode(value)
      : Buffer.from(value, 'utf8');
    for (const byte of bytes) {
      hash ^= byte;
      hash = Math.imul(hash, 0x01000193) >>> 0;
    }
    return hash.toString(16).padStart(8, '0');
  }

  function canonicalSeedItems(items) {
    if (!pricing) throw new Error('Probier pricing identity is unavailable');
    return pricing.normalizeItems(items).map((item) => ({
      ...item,
      properties: Object.fromEntries(Object.entries(item.properties)
        // These existing display/fulfilment markers do not participate in the
        // current native fixed-discount price. Every other property is retained.
        .filter(([key]) => !key.startsWith('_judes_bundle_')))
    }));
  }

  function seedFingerprint(selection) {
    return pricing.fingerprint({
      country: selection.country,
      currency: selection.currency,
      discountCode: selection.discountCode,
      items: canonicalSeedItems(selection.items)
    });
  }

  function catalogVersion(items, catalogPrices) {
    const prices = catalogPrices && typeof catalogPrices === 'object' ? catalogPrices : {};
    const rows = canonicalSeedItems(items).map((item) => {
      const price = Number(prices[item.id]);
      if (!Number.isSafeInteger(price) || price < 0) throw new Error('Invalid current catalog price');
      return [item.id, item.quantity, price];
    });
    return `catalog-fnv1a32-${fnv1a(stableStringify(rows))}`;
  }

  function expected(selection) {
    if (!selection || typeof selection !== 'object') throw new Error('Missing seed selection');
    const value = {
      country: String(selection.country || '').toUpperCase(),
      currency: String(selection.currency || '').toUpperCase(),
      priceGroupId: String(selection.priceGroupId || ''),
      productId: String(selection.productId || ''),
      recipeId: String(selection.recipeId || ''),
      recipeVersion: String(selection.recipeVersion || ''),
      catalogVersion: catalogVersion(selection.items, selection.catalogPrices),
      pricingIdentityVersion: String(selection.pricingIdentityVersion || ''),
      basketFingerprint: seedFingerprint(selection),
      sourceDigest: String(selection.sourceDigest || ''),
      sourceVersion: String(selection.sourceVersion || '')
    };
    if (!/^[A-Z]{2}$/.test(value.country) || !/^[A-Z]{3}$/.test(value.currency)
      || !identityFields.slice(2).every((field) => value[field])) throw new Error('Invalid seed selection');
    return value;
  }

  function manifestKey(value) {
    const raw = JSON.stringify(identityFields.map((field) => String(value[field] || '')));
    return `v2:${fnv1a(raw)}${fnv1a([...raw].reverse().join(''))}`;
  }

  function validateManifest(manifest) {
    if (!manifest || manifest.schemaVersion !== 2 || manifest.scope !== 'probier-initial-empty-cart-pilot'
      || manifest.complete !== true || typeof manifest.generationId !== 'string' || !manifest.generationId
      || !/^[a-f0-9]{64}$/.test(manifest.sourceDigest)
      || manifest.sourceVersion !== `admin-source-sha256:${manifest.sourceDigest}`
      || !/^[a-f0-9]{64}$/.test(manifest.localSourceDigest)
      || !Number.isSafeInteger(manifest.generatedAt) || !Number.isSafeInteger(manifest.expiresAt)
      || !Number.isSafeInteger(manifest.maxAgeMs) || manifest.maxAgeMs <= 0 || manifest.maxAgeMs > MAX_AGE_MS
      || manifest.expiresAt <= manifest.generatedAt || manifest.expiresAt - manifest.generatedAt > manifest.maxAgeMs
      || !manifest.countryPriceGroups || typeof manifest.countryPriceGroups !== 'object'
      || stableStringify(manifest.countryPriceGroups) !== stableStringify(EXPECTED_COUNTRY_PRICE_GROUPS)
      || !Array.isArray(manifest.requiredRecipeIds)
      || stableStringify([...manifest.requiredRecipeIds].sort()) !== stableStringify(EXPECTED_RECIPE_IDS)
      || !manifest.records || typeof manifest.records !== 'object' || Array.isArray(manifest.records)
      || manifest.recordCount !== 44 || Object.keys(manifest.records).length !== 44
      || !manifest.renderIndex || typeof manifest.renderIndex !== 'object' || Array.isArray(manifest.renderIndex)
      || Object.keys(manifest.renderIndex).length !== 11) return null;
    const seen = new Set();
    for (const [marketKey, priceGroupId] of Object.entries(EXPECTED_COUNTRY_PRICE_GROUPS)) {
      const [country, currency] = marketKey.split('|');
      const index = manifest.renderIndex[marketKey];
      if (!index || typeof index !== 'object' || Array.isArray(index)
        || stableStringify(Object.keys(index).sort()) !== stableStringify(EXPECTED_RECIPE_IDS)) return null;
      for (const recipeId of EXPECTED_RECIPE_IDS) {
        const key = index[recipeId];
        const record = manifest.records[key];
        const combination = `${marketKey}|${recipeId}`;
        if (seen.has(combination) || typeof key !== 'string' || !record
          || record.schemaVersion !== 2 || record.scope !== 'initial-empty-cart'
          || record.generationId !== manifest.generationId
          || record.sourceDigest !== manifest.sourceDigest || record.sourceVersion !== manifest.sourceVersion
          || record.generatedAt !== manifest.generatedAt || record.expiresAt !== manifest.expiresAt
          || record.country !== country || record.currency !== currency || record.priceGroupId !== priceGroupId
          || record.productId !== PRODUCT_ID || record.recipeId !== recipeId
          || !identityFields.every((field) => typeof record[field] === 'string' && record[field])
          || manifestKey(record) !== key
          || !['retailValueMinor', 'finalPriceMinor', 'discountAmountMinor']
            .every((field) => Number.isSafeInteger(record[field]) && record[field] >= 0)
          || record.retailValueMinor - record.discountAmountMinor !== record.finalPriceMinor) return null;
        seen.add(combination);
      }
    }
    if (seen.size !== 44 || new Set(Object.values(manifest.renderIndex).flatMap((index) => Object.values(index))).size !== 44) return null;
    return manifest;
  }

  function parse(raw) {
    if (typeof raw !== 'string' || byteLength(raw) > MAX_MANIFEST_BYTES) return null;
    try {
      const manifest = JSON.parse(raw);
      return validateManifest(manifest);
    } catch (error) {
      return null;
    }
  }

  // Retained for the earlier one-shot scaffold. The Probier pilot uses the
  // stricter manifest-level resolver below.
  function validate(record, wanted, now = Date.now()) {
    const fields = ['generationId', 'productId', 'recipeId', 'recipeVersion', 'catalogVersion',
      'discountVersion', 'pricingIdentityVersion', 'country', 'currency', 'basketFingerprint'];
    if (!record || record.schemaVersion !== 1 || record.scope !== 'initial-empty-cart' || !wanted
      || !fields.every((field) => typeof wanted[field] === 'string' && wanted[field]
        && record[field] === wanted[field])
      || !Number.isSafeInteger(wanted.maxAgeMs) || wanted.maxAgeMs <= 0
      || !Number.isSafeInteger(record.generatedAt) || !Number.isSafeInteger(record.expiresAt)
      || record.generatedAt > now || record.expiresAt <= now || record.expiresAt <= record.generatedAt
      || now - record.generatedAt > wanted.maxAgeMs
      || record.expiresAt - record.generatedAt > wanted.maxAgeMs) return null;
    const money = ['retailValueMinor', 'finalPriceMinor', 'discountAmountMinor'];
    if (!money.every((field) => Number.isSafeInteger(record[field]) && record[field] >= 0)
      || record.retailValueMinor - record.discountAmountMinor !== record.finalPriceMinor) return null;
    return Object.freeze({
      currency: record.currency,
      retailValueMinor: record.retailValueMinor,
      finalPriceMinor: record.finalPriceMinor,
      discountAmountMinor: record.discountAmountMinor,
      displayValid: true,
      checkoutVerified: false,
      generationId: record.generationId,
      expiresAt: record.expiresAt
    });
  }

  function resolve(manifest, selection, now = Date.now()) {
    try {
      if (!validateManifest(manifest) || manifest.generatedAt > now || manifest.expiresAt <= now
        || now - manifest.generatedAt > manifest.maxAgeMs) return null;
      const marketKey = `${String(selection.country || '').toUpperCase()}|${String(selection.currency || '').toUpperCase()}`;
      const priceGroupId = manifest.countryPriceGroups[marketKey];
      if (typeof priceGroupId !== 'string' || !priceGroupId
        || (selection.priceGroupId && selection.priceGroupId !== priceGroupId)) return null;
      const wanted = expected({
        ...selection,
        priceGroupId,
        sourceDigest: manifest.sourceDigest,
        sourceVersion: manifest.sourceVersion
      });
      if (!manifest.requiredRecipeIds.includes(wanted.recipeId)) return null;
      const record = manifest.records[manifestKey(wanted)];
      if (!record || record.schemaVersion !== 2 || record.scope !== 'initial-empty-cart'
        || record.generationId !== manifest.generationId
        || record.generatedAt !== manifest.generatedAt || record.expiresAt !== manifest.expiresAt
        || !identityFields.every((field) => record[field] === wanted[field])) return null;
      const money = ['retailValueMinor', 'finalPriceMinor', 'discountAmountMinor'];
      if (!money.every((field) => Number.isSafeInteger(record[field]) && record[field] >= 0)
        || record.retailValueMinor - record.discountAmountMinor !== record.finalPriceMinor) return null;
      return Object.freeze({
        currency: record.currency,
        retailValueMinor: record.retailValueMinor,
        finalPriceMinor: record.finalPriceMinor,
        discountAmountMinor: record.discountAmountMinor,
        displayValid: true,
        checkoutVerified: false,
        generationId: record.generationId,
        expiresAt: record.expiresAt,
        country: record.country,
        currency: record.currency,
        recipeId: record.recipeId,
        basketFingerprint: record.basketFingerprint,
        catalogVersion: record.catalogVersion,
        sourceDigest: record.sourceDigest,
        sourceVersion: record.sourceVersion,
        recordKey: manifestKey(record)
      });
    } catch (error) {
      return null;
    }
  }

  function serverNodeMatches(seed, dataset) {
    if (!seed || !dataset) return false;
    return dataset.generationId === seed.generationId
      && dataset.sourceDigest === seed.sourceDigest
      && dataset.sourceVersion === seed.sourceVersion
      && dataset.recipeId === seed.recipeId
      && dataset.basketFingerprint === seed.basketFingerprint
      && dataset.catalogVersion === seed.catalogVersion
      && dataset.country === seed.country
      && dataset.currency === seed.currency
      && Number(dataset.expiresAt) === seed.expiresAt
      && Number(dataset.finalPriceMinor) === seed.finalPriceMinor;
  }

  async function refreshDisplay(options) {
    const seed = options.seed || null;
    let expiryTimer = null;
    options.setReady(false);
    if (seed) {
      options.paintSeed(seed);
      const delay = Math.min(2147483647, Math.max(0, seed.expiresAt - Date.now()));
      expiryTimer = (options.setTimeoutFn || root.setTimeout)?.(() => {
        if (options.isCurrent()) {
          options.paintFallback();
          options.setReady(false);
        }
      }, delay);
      if (expiryTimer && typeof expiryTimer.unref === 'function') expiryTimer.unref();
    }
    try {
      const exact = await options.quote();
      if (!options.isCurrent()) return null;
      if (expiryTimer) (options.clearTimeoutFn || root.clearTimeout)?.(expiryTimer);
      options.paintExact(exact);
      options.setReady(true);
      return exact;
    } catch (error) {
      if (options.isCurrent() && !seed) options.paintFallback(error);
      throw error;
    }
  }

  return Object.freeze({
    MAX_MANIFEST_BYTES,
    MAX_AGE_MS,
    canonicalSeedItems,
    seedFingerprint,
    catalogVersion,
    expected,
    manifestKey,
    parse,
    validateManifest,
    validate,
    resolve,
    refreshDisplay,
    serverNodeMatches,
    EXPECTED_COUNTRY_PRICE_GROUPS,
    EXPECTED_RECIPE_IDS,
    stableStringify
  });
}));
